import { useState, useRef, useEffect } from 'react';

const MIN_INSTRUCTIONS_HEIGHT = 200;
const HEADER_HEIGHT = 40;
const DEFAULT_SECTION_HEIGHT = 200;
const MIN_SECTION_HEIGHT = 100;

/**
 * UI-only hook for resizing collapsible left-panel sections (Function Library, Parameters).
 */
export function useResizableSection(panelRef, {
    isFunctionLibraryExpanded,
    isParametersExpanded,
}) {
    const [functionLibraryHeight, setFunctionLibraryHeight] = useState(DEFAULT_SECTION_HEIGHT);
    const [parametersHeight, setParametersHeight] = useState(DEFAULT_SECTION_HEIGHT);

    const isDraggingFunctionRef = useRef(false);
    const isDraggingParametersRef = useRef(false);
    const startYRef = useRef(0);
    const startHeightRef = useRef(0);

    const handleResizeStart = (section, e) => {
        e.preventDefault();
        if (section === 'function') {
            isDraggingFunctionRef.current = true;
            startHeightRef.current = functionLibraryHeight;
        } else {
            isDraggingParametersRef.current = true;
            startHeightRef.current = parametersHeight;
        }
        startYRef.current = e.clientY;
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!panelRef.current) return;

            const panelHeight = panelRef.current.clientHeight;
            const totalHeadersHeight = HEADER_HEIGHT * 3;
            const availableForResizableSections =
                panelHeight - totalHeadersHeight - MIN_INSTRUCTIONS_HEIGHT;

            if (isDraggingFunctionRef.current) {
                const delta = e.clientY - startYRef.current;
                let maxAllowed = availableForResizableSections;
                if (isParametersExpanded) {
                    maxAllowed -= parametersHeight;
                }
                const newHeight = Math.max(
                    MIN_SECTION_HEIGHT,
                    Math.min(maxAllowed, startHeightRef.current - delta)
                );
                setFunctionLibraryHeight(newHeight);
            } else if (isDraggingParametersRef.current) {
                const delta = e.clientY - startYRef.current;
                let maxAllowed = availableForResizableSections;
                if (isFunctionLibraryExpanded) {
                    maxAllowed -= functionLibraryHeight;
                }
                const newHeight = Math.max(
                    MIN_SECTION_HEIGHT,
                    Math.min(maxAllowed, startHeightRef.current - delta)
                );
                setParametersHeight(newHeight);
            }
        };

        const handleMouseUp = () => {
            isDraggingFunctionRef.current = false;
            isDraggingParametersRef.current = false;
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        isFunctionLibraryExpanded,
        isParametersExpanded,
        functionLibraryHeight,
        parametersHeight,
        panelRef,
    ]);

    return {
        functionLibraryHeight,
        parametersHeight,
        handleResizeStart,
        minInstructionsHeight: MIN_INSTRUCTIONS_HEIGHT,
    };
}
